import { ArrowLeft, MoreHorizontal, Settings2, Trash2 } from "lucide-react";
import * as React from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Page, PageHeader } from "@/components/layout/page";
import { useTour } from "@/components/layout/tour";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Callout, EmptyState, ErrorState, Spinner } from "@/components/ui/feedback";
import { CopyButton, Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/misc";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/components/ui/toast";
import { CampaignAbandonment } from "@/features/campaign-abandonment";
import { CampaignCadence } from "@/features/campaign-cadence";
import { CampaignDialing } from "@/features/campaign-dialing";
import { DeleteCampaignDialog } from "@/features/campaign-delete";
import { CampaignDispositions } from "@/features/campaign-dispositions";
import { CampaignFormDialog } from "@/features/campaign-form";
import { CampaignLines } from "@/features/campaign-lines";
import { CampaignNumbers } from "@/features/campaign-numbers";
import { CampaignRepNumbers } from "@/features/campaign-rep-numbers";
import { CampaignRepTexts } from "@/features/campaign-rep-texts";
import { CampaignLists } from "@/features/campaign-lists";
import { CampaignStaffing } from "@/features/campaign-staffing";
import { ApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import {
  CAMPAIGN_STATUSES, CAMPAIGN_TABS, type CampaignTab, dialModeText,
  dialingContradictsPurpose, purposeText, statusText, visibleTabs,
} from "@/lib/campaign";
import { useCampaign, useUpdateCampaign } from "@/lib/queries";
import type { Tour } from "@/lib/tours";
import { atLeast } from "@/lib/types";

/**
 * One campaign, and everything about how it is worked (ADR-014, -017, -019).
 *
 * Tabs rather than one long form, because the halves change at different times: routing and
 * staffing when the floor changes, dispositions when the firm's reporting does, dialing and lists
 * when a batch of leads arrives. Each tab saves on its own, so a half-edited dialing form never
 * rides along with a staffing change. The status sits in the header instead, because it is the
 * one switch that starts and stops the dialer and it should not be three clicks deep.
 *
 * The tab is in the URL (`?tab=lists`), so a reload or a shared link lands where the reader was.
 * Supervisors read every tab; only an admin changes anything — core-api enforces that, and the
 * controls follow it.
 */

const TAB_LABELS: Record<CampaignTab, string> = {
  routing: "Routing",
  numbers: "Numbers",
  staffing: "Staffing",
  dispositions: "Dispositions",
  dialing: "Dialing",
  cadence: "Cadence",
  lists: "Lists",
};

/** What each tab is for, as the screen's tour says it. */
const TAB_TOUR: Record<CampaignTab, string> = {
  routing:
    "Where this campaign's calls wait for a rep: its lines, how long a caller waits and what they hear. It also says what happens when a caller dials a rep's own number and that rep cannot take the call.",
  numbers:
    "Every number this campaign calls from and takes calls on: its primary number, its local numbers and its reps' own. A number flagged as spam or replaced keeps taking callbacks, and no call presents it.",
  staffing:
    "Who takes this campaign's calls: the reps on each line, and each rep's own number. Whether their phones answer its calls by themselves is set here too.",
  dispositions: "The outcomes a rep picks from after a call, in the order of their buttons.",
  dialing:
    "How the dialer calls this campaign's leads: the cadence it runs, the mode, the calling hours and the caller ID.",
  cadence:
    "The campaign's leads as the dialer sees them now: which are due, which are waiting, and what each one is waiting for.",
  lists: "The dial lists and the leads on them. A file of leads is imported here.",
};

/**
 * The screen's product tour (`components/layout/tour.tsx`): the header, then one step per tab.
 * Built from the tabs this campaign shows (`visibleTabs`), so an inbound campaign with the dialer
 * off gets no step for Dialing or Cadence. A tab's step opens the tab (`search`) and points at
 * its name.
 */
function campaignTour(tabs: readonly CampaignTab[]): Tour {
  return {
    id: "campaign",
    version: 1,
    steps: [
      {
        target: "campaign-header",
        title: "This campaign",
        body: "Its name and status. The line under them shows its slug, its state and language, how many numbers ring it and what the dialer is set to.",
      },
      {
        target: "campaign-actions",
        title: "Start, stop and edit",
        body: "Status starts and stops the dialer on this campaign. Edit details changes its name, state, language and what it is for. The menu deletes it.",
        minimum: "admin",
        align: "end",
      },
      ...tabs.map((name) => ({
        target: `campaign-tab-${name}`,
        title: TAB_LABELS[name],
        body: TAB_TOUR[name],
        search: { tab: name === "routing" ? "" : name },
      })),
    ],
  };
}

export function CampaignPage() {
  const { campaignId } = useParams();
  const [params, setParams] = useSearchParams();
  const requested = params.get("tab") ?? "";
  const navigate = useNavigate();
  const toast = useToast();
  const { user } = useAuth();
  const canManage = atLeast(user?.role, "admin");
  const { data: campaign, isPending, error, refetch } = useCampaign(campaignId);
  // What this campaign shows (ADR-060). A link to a tab it does not show — a bookmark from
  // before someone labelled it inbound — lands on Routing rather than on nothing.
  const tabs = campaign ? visibleTabs(campaign) : CAMPAIGN_TABS;
  const tab: CampaignTab = (tabs as readonly string[]).includes(requested)
    ? (requested as CampaignTab)
    : "routing";
  const update = useUpdateCampaign();
  const [editing, setEditing] = React.useState(false);
  const [deleting, setDeleting] = React.useState(false);

  // Keyed on the tabs' names: `visibleTabs` may answer a new list on every render, and a tour
  // rebuilt on every render would register itself for ever.
  const tabKey = tabs.join(",");
  const tour = React.useMemo(() => campaignTour(tabKey.split(",") as CampaignTab[]), [tabKey]);
  useTour(tour, { ready: Boolean(campaign) });

  if (isPending) {
    return (
      <Page>
        <div className="flex min-h-[40vh] items-center justify-center">
          <Spinner className="size-5" />
        </div>
      </Page>
    );
  }

  if (error || !campaign) {
    const missing = error instanceof ApiError && error.status === 404;
    return (
      <Page>
        {missing ? (
          <EmptyState
            className="min-h-[60vh]"
            title="Campaign not found"
            description="It may have been deleted, or it belongs to another organisation."
            action={
              <Button variant="outline" asChild>
                <Link to="/campaigns">Back to campaigns</Link>
              </Button>
            }
          />
        ) : (
          <ErrorState
            className="min-h-[60vh]"
            error={error}
            title="Could not load the campaign"
            action={
              <Button variant="outline" onClick={() => void refetch()}>
                Try again
              </Button>
            }
          />
        )}
      </Page>
    );
  }

  const status = statusText(campaign.status);

  const changeStatus = (next: string) => {
    update.mutate(
      { id: campaign.id, status: next },
      {
        onSuccess: () =>
          toast.success(`${statusText(next).label}`, `${campaign.name}: ${statusText(next).description}`),
        onError: (e) =>
          toast.error(
            "Could not change the status",
            e instanceof ApiError ? e.message : "Please try again.",
          ),
      },
    );
  };

  return (
    <Page>
      <Link
        to="/campaigns"
        className="mb-3 inline-flex items-center gap-1 text-xs text-text-muted hover:text-text"
      >
        <ArrowLeft className="size-3.5" aria-hidden />
        Campaigns
      </Link>

      <PageHeader
        tour="campaign-header"
        title={
          <span className="inline-flex flex-wrap items-center gap-2">
            {campaign.name}
            <Badge tone={status.tone}>{status.label}</Badge>
            {!campaign.enabled && <Badge tone="warning">Disabled</Badge>}
          </span>
        }
        description={
          <span className="inline-flex flex-wrap items-center gap-x-3 gap-y-1">
            <span className="inline-flex items-center gap-1 font-mono text-xs">
              {campaign.slug}
              <CopyButton value={campaign.slug} label="Copy slug" />
            </span>
            <span>
              {campaign.state ?? "No state"} · {campaign.language === "es" ? "Spanish" : "English"}
            </span>
            <span>
              {campaign.did_count} number{campaign.did_count === 1 ? "" : "s"}
            </span>
            <span>Dialer: {dialModeText(campaign.dial_mode).label}</span>
            {user?.role === "superadmin" && campaign.tenant_name && <span>{campaign.tenant_name}</span>}
          </span>
        }
        action={
          canManage && (
            <div className="flex items-center gap-2" data-tour="campaign-actions">
              <Select value={campaign.status} onValueChange={changeStatus} disabled={update.isPending}>
                <SelectTrigger className="w-32" aria-label="Status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent align="end">
                  {CAMPAIGN_STATUSES.map((s) => (
                    <SelectItem key={s} value={s} description={statusText(s).description}>
                      {statusText(s).label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button variant="outline" onClick={() => setEditing(true)}>
                <Settings2 />
                Edit details
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" aria-label="More actions">
                    <MoreHorizontal />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem destructive onSelect={() => setDeleting(true)}>
                    <Trash2 />
                    Delete campaign
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )
        }
      />

      {dialingContradictsPurpose(campaign) && (
        <Callout tone="warning">
          This campaign is marked {purposeText(campaign.purpose).label.toLowerCase()}, but its
          dialer is set to {dialModeText(campaign.dial_mode).label.toLowerCase()} — so it is
          calling this campaign&apos;s lists. The Dialing and Lists tabs stay below because of it.
          Set the mode to Off, or mark the campaign outbound.
        </Callout>
      )}

      <Tabs
        value={tab}
        onValueChange={(next) => setParams(next === "routing" ? {} : { tab: next }, { replace: true })}
      >
        <TabsList>
          {tabs.map((name) => (
            <TabsTrigger key={name} value={name} data-tour={`campaign-tab-${name}`}>
              {TAB_LABELS[name]}
            </TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value="routing" className="space-y-4 pt-4">
          <CampaignLines campaign={campaign} canManage={canManage} />
          <CampaignRepNumbers campaign={campaign} canManage={canManage} />
          {/* Only where core-api keeps texts: an older one has no such setting to save. */}
          {campaign.rep_text_fallback !== undefined && (
            <CampaignRepTexts campaign={campaign} canManage={canManage} />
          )}
        </TabsContent>
        <TabsContent value="numbers" className="pt-4">
          <CampaignNumbers campaign={campaign} canManage={canManage} />
        </TabsContent>
        <TabsContent value="staffing" className="pt-4">
          <CampaignStaffing campaign={campaign} canManage={canManage} />
        </TabsContent>
        <TabsContent value="dispositions" className="pt-4">
          <CampaignDispositions campaign={campaign} canManage={canManage} />
        </TabsContent>
        {tabs.includes("dialing") && (
          <TabsContent value="dialing" className="space-y-4 pt-4">
            {/* What the dialer stops the campaign on, above how it dials it (platform ADR-163). */}
            <CampaignAbandonment campaign={campaign} />
            <CampaignDialing campaign={campaign} canManage={canManage} />
          </TabsContent>
        )}
        {tabs.includes("cadence") && (
          <TabsContent value="cadence" className="pt-4">
            <CampaignCadence campaign={campaign} canManage={canManage} />
          </TabsContent>
        )}
        {tabs.includes("lists") && (
          <TabsContent value="lists" className="pt-4">
            <CampaignLists campaign={campaign} canManage={canManage} />
          </TabsContent>
        )}
      </Tabs>

      <CampaignFormDialog open={editing} onOpenChange={setEditing} campaign={campaign} />

      <DeleteCampaignDialog
        open={deleting}
        onOpenChange={setDeleting}
        campaign={campaign}
        onDeleted={() => navigate("/campaigns", { replace: true })}
      />
    </Page>
  );
}
